import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { themes, countries, citiesByCountry, type Country } from "@/data/photographers";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

export const Route = createFileRoute("/brief")({
  validateSearch: (search: Record<string, unknown>) => ({
    theme: typeof search.theme === "string" ? search.theme : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Anfrage senden, OM Selected" },
      { name: "description", content: "Erzähl uns anonym, was du suchst. Anlass, Region, Budget." },
      { property: "og:title", content: "Anfrage senden, OM Selected" },
      { property: "og:description", content: "Anonyme Anfrage für deinen Foto-Auftrag." },
      { property: "og:url", content: "https://omselected.art/brief" },
    ],
    links: [{ rel: "canonical", href: "https://omselected.art/brief" }],
  }),
  component: BriefPage,
});

function BriefPage() {
  const { theme: presetTheme } = Route.useSearch();
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [country, setCountry] = useState<Country | "">("");
  const [cityChoice, setCityChoice] = useState<string>("");
  const [wantPhotographer, setWantPhotographer] = useState(true);
  const [wantVisagist, setWantVisagist] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    const cityValue = (cityChoice === "__other__" ? String(fd.get("city_other") ?? "") : cityChoice).trim();
    const regionValue = [cityValue, country].filter(Boolean).join(", ");
    const creatorTypes: ("photographer" | "visagist")[] = [];
    if (wantPhotographer) creatorTypes.push("photographer");
    if (wantVisagist) creatorTypes.push("visagist");
    if (creatorTypes.length === 0) {
      setError(t("briefPage.errSelect"));
      return;
    }
    const schema = z.object({
      occasion: z.string().trim().min(1).max(200),
      theme: z.string().trim().max(80).optional(),
      region: z.string().trim().max(80).optional(),
      budget_range: z.string().trim().max(120).optional(),
      description: z.string().trim().min(10).max(4000),
      contact_email: z.string().trim().email().max(255),
      contact_name: z.string().trim().min(1).max(120),
    });
    const parsed = schema.safeParse({
      occasion: fd.get("occasion"),
      theme: fd.get("theme") || undefined,
      region: regionValue || undefined,
      budget_range: fd.get("budget_range") || undefined,
      description: fd.get("description"),
      contact_email: fd.get("contact_email"),
      contact_name: fd.get("contact_name"),
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("briefPage.errCheckFields"));
      return;
    }
    setBusy(true);
    const { error } = await supabase.from("client_briefs").insert({ ...parsed.data, creator_types: creatorTypes });
    setBusy(false);
    if (error) { setError(error.message); return; }
    setSent(true);
  }

  return (
    <div>
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 pt-12 pb-16">
          <div className="eyebrow text-muted-foreground">{t("briefPage.eyebrow")}</div>
          <h1 className="serif text-5xl md:text-7xl mt-6 leading-[0.95]">{t("briefPage.titleLine1")}<br />{t("briefPage.titleLine2Pre")}<em className="italic">{t("briefPage.titleEm")}</em>{t("briefPage.titleAfter")}</h1>
          <p className="mt-8 max-w-xl text-muted-foreground">{t("briefPage.intro")}</p>
        </div>
      </section>
      <section>
        <div className="mx-auto max-w-[900px] px-5 md:px-10 py-16">
          {sent ? (
            <div className="border border-border p-12 text-center">
              <div className="eyebrow text-muted-foreground">{t("briefPage.thanksEyebrow")}</div>
              <h2 className="serif text-3xl md:text-4xl mt-4">{t("briefPage.thanksTitle")}</h2>
              <p className="mt-4 text-muted-foreground">{t("briefPage.thanksText")}</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-10">
              <Field label={t("briefPage.whatLabel")} hint={t("briefPage.whatHint")}>
                <div className="flex flex-wrap gap-6 pt-2 text-sm">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={wantPhotographer} onChange={(e) => setWantPhotographer(e.target.checked)} /> {t("briefPage.kindPhotographer")}
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={wantVisagist} onChange={(e) => setWantVisagist(e.target.checked)} /> {t("briefPage.kindVisagist")}
                  </label>
                </div>
              </Field>
              <Field label={t("briefPage.occasionLabel")} hint={t("briefPage.occasionHint")}>
                <input name="occasion" required className="w-full border-b border-foreground bg-transparent py-2 outline-none" placeholder={t("briefPage.occasionPlaceholder")} />
              </Field>
              <Field label={t("briefPage.themeLabel")}>
                <select name="theme" defaultValue={presetTheme ?? ""} className="w-full border-b border-foreground bg-transparent py-2 outline-none">
                  {themes.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </Field>
              <Field label={t("briefPage.countryLabel")}>
                <select
                  value={country}
                  onChange={(e) => { setCountry(e.target.value as Country | ""); setCityChoice(""); }}
                  className="w-full border-b border-foreground bg-transparent py-2 outline-none"
                >
                  <option value="">{t("briefPage.countryAny")}</option>
                  {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label={t("briefPage.cityLabel")}>
                <select
                  value={cityChoice}
                  onChange={(e) => setCityChoice(e.target.value)}
                  disabled={!country}
                  className="w-full border-b border-foreground bg-transparent py-2 outline-none disabled:opacity-50"
                >
                  <option value="">{country ? t("briefPage.cityAny") : t("briefPage.cityFirstCountry")}</option>
                  {country && citiesByCountry[country].map((c) => <option key={c} value={c}>{c}</option>)}
                  {country && <option value="__other__">{t("briefPage.cityOther")}</option>}
                </select>
                {cityChoice === "__other__" && (
                  <input
                    name="city_other"
                    placeholder={t("briefPage.cityEnter")}
                    maxLength={80}
                    className="mt-3 w-full border-b border-foreground bg-transparent py-2 outline-none"
                  />
                )}
              </Field>
              <Field label={t("briefPage.budgetLabel")} hint={t("briefPage.budgetHint")}>
                <input name="budget_range" className="w-full border-b border-foreground bg-transparent py-2 outline-none" placeholder={t("briefPage.budgetPlaceholder")} />
              </Field>
              <Field label={t("briefPage.descLabel")}>
                <textarea name="description" required minLength={10} rows={5} className="w-full border-b border-foreground bg-transparent py-2 outline-none resize-none" placeholder={t("briefPage.descPlaceholder")} />
              </Field>
              <Field label={t("briefPage.contactLabel")} hint={t("briefPage.contactHint")}>
                <input name="contact_email" type="email" required className="w-full border-b border-foreground bg-transparent py-2 outline-none" placeholder="du@example.com" />
              </Field>
              <Field label={t("briefPage.nameLabel")} hint={t("briefPage.nameHint")}>
                <input name="contact_name" required maxLength={120} className="w-full border-b border-foreground bg-transparent py-2 outline-none" placeholder={t("briefPage.namePlaceholder")} />
              </Field>
              {error && <div className="text-sm text-destructive">{error}</div>}
              <button type="submit" disabled={busy} className="eyebrow border border-foreground px-6 py-3 hover:bg-foreground hover:text-background transition-colors disabled:opacity-50">{busy ? t("briefPage.submitBusy") : t("briefPage.submit")}</button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="eyebrow text-muted-foreground mb-2">{label}</div>
      {children}
      {hint && <div className="mt-2 text-xs italic text-muted-foreground">{hint}</div>}
    </label>
  );
}